import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import { usersApi } from '../../api/users.api';

const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' }
];

export default function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    profile: {
      language: 'en'
    }
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      usersApi.getById(id).then((res) => {
        const user = res.data;
        setFormData({
          username: user.username || '',
          email: user.emails?.[0]?.address || '',
          password: '',
          profile: {
            language: user.profile?.language || 'en'
          }
        });
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('profile.')) {
      const field = name.replace('profile.', '');
      setFormData((prev) => ({
        ...prev,
        profile: { ...prev.profile, [field]: value }
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        await usersApi.update(id, formData);
      } else {
        await usersApi.create(formData);
      }
      navigate('/users');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit User' : 'Create User'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={isEdit}
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <Select
              label="Language"
              name="profile.language"
              value={formData.profile.language}
              onChange={handleChange}
              options={languageOptions}
            />
            {!isEdit && (
              <Input
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required={!isEdit}
              />
            )}
          </div>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {isEdit ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
